/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { MapViewProps } from './MapView.types';
import { StyledMapView } from './MapView.styles';
import { MapClustersComponent } from '../../molecules/MapClustersComponent';
import { eventUtil, MapComponent } from '@marriott/mi-ui-library';
import { GOOGLE_MAP_VAL_KEY } from '@marriott/shared/mi-helper-utils';
import clsx from 'clsx';

// Use named rather than default exports.
export const MapView: FC<MapViewProps> = props => {
  /** Internal handle for map panning (e.g. mobile marker updates); parent receives the instance via onMapReady. */
  const googleMapInstanceRef = useRef<google.maps.Map | null>(null);

  /**
   * State's Here...
   */
  const [openInfoWindow, setOpenInfoWindow] = useState(false);
  const [showSlider, setShowSlider] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<any | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number>(0);

  /**
   * Reference of map to skip the accessibility of the subtree of the entire map...
   */
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const mapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: true,
    gestureHandling: 'cooperative',
    mapId: 'ec5bb275520a7fc8',
  };

  const mapStyle = {
    height: props?.isAuthorModeEnabled ? '500px' : '68vh',
    width: '100%',
  };

  const closeInfoWindow = () => {
    setOpenInfoWindow(false);
    setShowSlider(false);
  };

  const markersData = props?.markers ?? [];

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      googleMapInstanceRef.current = map;
      props.onMapReady?.(map);
      const markersLatLng = markersData?.map((property: any) => {
        return {
          lat: property?.node?.basicInformation?.latitude,
          lng: property?.node?.basicInformation?.longitude,
        };
      });

      // set boundaries for map based on lat/lng coordinates of markers
      const bounds = new window.google.maps.LatLngBounds();
      if (markersLatLng.length > 0) {
        markersLatLng.forEach(({ lat, lng }: { lat: any; lng: any }) =>
          bounds.extend(new window.google.maps.LatLng(lat, lng))
        );
        map.fitBounds(bounds);
      } else {
        bounds.extend(new window.google.maps.LatLng(props.mapCenter.lat, props.mapCenter.lng));
        map.fitBounds(bounds);
      }
      setTimeout(function () {
        const maxZoom = 13;
        if (markersData.length === 1 && (map.getZoom() || 24 > maxZoom)) {
          map.setZoom(maxZoom);
        }
      }, 250);
    },
    [props.markers]
  );

  // Hide map from NVDA/screen readers and remove all descendants from tab order
  // (allows clicks/interaction but skips focus for accessibility)
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;

    el?.setAttribute('aria-hidden', 'true');
    el?.setAttribute('tabindex', '-1');

    const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';

    const makeDescendantsNonFocusable = () => {
      el?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)?.forEach(node => {
        node?.setAttribute('tabindex', '-1');
      });
    };

    makeDescendantsNonFocusable();

    const observer = new MutationObserver(makeDescendantsNonFocusable);
    observer?.observe(el, { childList: true, subtree: true });

    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    eventUtil.on('updateActiveMarker', (updatedActiveMarker: any) => {
      if (props?.viewPort?.includes('Mobile')) {
        const mapPan = (map: google.maps.Map) => {
          const bounds = new window.google.maps.LatLngBounds();
          bounds.extend(
            new window.google.maps.LatLng(
              updatedActiveMarker?.node?.basicInformation?.latitude,
              updatedActiveMarker?.node?.basicInformation?.longitude
            )
          );
          map.fitBounds(bounds);
          setTimeout(function () {
            const maxZoom = 16;
            if (map.getZoom() || 24 > maxZoom) {
              map.setZoom(maxZoom);
            }
          }, 350);
        };
        const loadedMap = googleMapInstanceRef.current;
        if (loadedMap) {
          mapPan(loadedMap);
        }
      }
    });
  }, [currentSlideIndex]);

  return (
    <StyledMapView
      ref={mapContainerRef}
      data-testid="luxury-MapView"
      data-component-name="m-luxury-MapView"
      $isAuthorModeEnabled={props?.isAuthorModeEnabled}
    >
      {props?.isLoading ? (
        <div className="loader-container d-flex align-items-center justify-content-center" data-testid="facets-spinner">
          <div className="m-spinner-m"></div>
        </div>
      ) : (
        <MapComponent
          googleMapsApiKey={GOOGLE_MAP_VAL_KEY}
          mapId={mapOptions.mapId}
          mapContainerStyle={mapStyle}
          mapContainerClassName={clsx(showSlider ? 'hide-controls' : '')}
          onLoad={onMapLoad}
          options={mapOptions}
          onCenterChanged={props?.handleCenterChange}
          mapHeight={'100%'}
          onClick={closeInfoWindow}
          onDragStart={closeInfoWindow}
          clickableIcons={false}
        >
          {markersData && (
            <MapClustersComponent
              markers={markersData}
              openInfoWindow={openInfoWindow}
              setOpenInfoWindow={setOpenInfoWindow}
              showSlider={showSlider}
              setShowSlider={setShowSlider}
              ctaLabel={props?.ctaLabel}
              showReviews={props?.showReviews}
              viewPort={props?.viewPort}
              requestID={props?.requestID}
              currentLocale={props?.currentLocale}
              client={props?.client}
              selectedMarker={selectedMarker}
              setSelectedMarker={setSelectedMarker}
              setCurrentSlideIndex={setCurrentSlideIndex}
              currentSlideIndex={currentSlideIndex}
            />
          )}
        </MapComponent>
      )}
    </StyledMapView>
  );
};
